import { useCallback, useState } from 'react';
import { EyeOutlined } from '@ant-design/icons';
import { Empty, Tooltip, Typography } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import RCTable from 'components/custom-table/RCTable';
import { fetchIntracityMarginPending } from 'store/approvalSliceV2';

const { Text } = Typography;

const DASH = '-';
const inr = (n) =>
	n === null || n === undefined || n === ''
		? DASH
		: `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// Plain coloured text, no chip: the colour alone carries the state, and a tinted block in
// every row of a status column is noise rather than emphasis.
const STAGE = {
	rejected: { color: '#c62828', label: 'Rejected' },
	created: { color: '#2e7d32', label: 'Trip Created' },
	vp: { color: '#1565c0', label: 'Awaiting VP Approval' },
	cm: { color: '#e08600', label: 'Pending City Manager' },
};

/** Where a submission ended up, across the whole chain. */
function marginStatusTag(record) {
	let stage = null;
	if (record?.status === 'Rejected') stage = STAGE.rejected;
	else if (record?.trip_id) stage = STAGE.created;
	else if (record?.vp_approval_status === 'Awaiting VP Approval') stage = STAGE.vp;
	else if (record?.status === 'Pending Approval') stage = STAGE.cm;

	if (!stage) return <Text>{record?.status || '-'}</Text>;
	return <Text style={{ color: stage.color, fontWeight: 500 }}>{stage.label}</Text>;
}

/**
 * Intracity adhoc trips held for margin approval.
 *
 * Below 5% margin no trip is created — an adhoc request is raised instead, holding the
 * submitted trip payload until the City Manager and then the VP approve, at which point
 * the trip is built for real. So this tab lists requests, not trips, and the row ids are
 * request ids.
 *
 * Approving happens on the existing Adhoc Request Details page, which already carries the
 * City Manager and VP buttons for exactly this chain.
 */
function PendingMarginTripsTab() {
	const location = useLocation();
	// BreadCrumbs honours location.state.from ahead of the route's fixed link, so the back
	// arrow on Request Details can return here instead of the Adhoc Request list. The tab
	// goes in the query string rather than router state so it survives a refresh and a
	// copied URL, and the existing params carry pagination back with it.
	const backTo = (() => {
		const params = new URLSearchParams(location.search);
		params.set('tab', 'pending');
		return `${location.pathname}?${params.toString()}`;
	})();

	// RCTable owns the fetch and the pagination; it calls dispatchAction with
	// {offset, limit} and reads {rows, count} back, same as every other list here.
	const [tableData, setTableData] = useState({});
	const [paginateConfig, setPaginateConfig] = useState({
		offset: 0,
		limit: 10,
		currentPage: 1,
	});

	const dispatchAction = useCallback(
		(params) => fetchIntracityMarginPending({ limit: params.limit, offset: params.offset }),
		[]
	);

	const columns = [
		{ title: 'Request ID', dataIndex: 'id', key: 'id', width: 110 },
		{ title: 'Project', dataIndex: 'project_name', key: 'project_name' },
		{
			// A project always has a city, so a blank here means something is wrong with the
			// record rather than simply being unset — say so instead of rendering an empty cell.
			title: 'City',
			dataIndex: 'city',
			key: 'city',
			width: 120,
			render: (city) => city || <Text type="secondary">Not available</Text>,
		},
		{ title: 'Client', dataIndex: 'client_name', key: 'client_name' },
		{ title: 'Vendor', dataIndex: 'vendor_name', key: 'vendor_name' },
		{
			title: 'Trip Date',
			dataIndex: 'expected_trip_date',
			key: 'expected_trip_date',
			width: 160,
		},
		{
			title: 'Client Rate',
			dataIndex: 'client_rate',
			key: 'client_rate',
			width: 120,
			render: inr,
		},
		{
			title: 'Vendor Rate',
			dataIndex: 'vendor_rate',
			key: 'vendor_rate',
			width: 120,
			render: inr,
		},
		{
			title: 'Margin',
			key: 'margin',
			width: 150,
			render: (_, record) => (
				<>
					<Text strong style={{ color: '#c62828' }}>
						{inr(record?.margin)}
					</Text>
					<Text style={{ display: 'block', fontSize: 12, color: '#666' }}>
						5% = {inr(Number(record?.vendor_rate ?? 0) * 0.05)}
					</Text>
				</>
			),
		},
		{
			// The tab carries every submission, not just in-flight ones, so where each one
			// ended up is the column that makes it readable.
			title: 'Status',
			key: 'status',
			width: 190,
			render: (_, record) => marginStatusTag(record),
		},
		{
			title: 'Action',
			key: 'action',
			fixed: 'right',
			width: 90,
			render: (_, record) => (
				<Tooltip title="View & approve">
					<Link to={`/trips/adhoc-request/${record.id}`} state={{ from: backTo }}>
						<EyeOutlined />
					</Link>
				</Tooltip>
			),
		},
	];

	return (
		<RCTable
			rowKey="id"
			tableDatas={[tableData, setTableData]}
			togglePaginate={[paginateConfig, setPaginateConfig]}
			dispatchAction={dispatchAction}
			columns={columns}
			scroll={{ x: true }}
			locale={{
				emptyText: (
					<Empty
						description={
							<Text type="secondary">No trips awaiting margin approval</Text>
						}
					/>
				),
			}}
		/>
	);
}

export default PendingMarginTripsTab;
